import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { useFormik } from 'formik';
import React, { useMemo } from 'react';

import { useDispatch } from 'react-redux';
import { customErrorShackBar } from '../../../../actions/snackbars/SnackBarsActions';
import { useHasAnyConnectedGateway } from '../../../../selectors/RestaurantSelectors';
import { getMaxCapacity, useAllTables } from '../../../entities/allTables';
import {
  PAYMENT_BANKS,
  PREPAY_AMOUNT_MODES,
  usePrepaySettings,
  useUpdatePrepaySettingsMutation,
} from '../../../entities/prepaySettings';

import { buildMinutesRangeOptions } from '../../../shared/lib/buildMinutesRangeOptions';
import {
  AMOUNT_PER_GUEST_MAX,
  AMOUNT_PER_GUEST_MIN,
  AUTO_CANCEL_DELAY_MAX,
  AUTO_CANCEL_DELAY_MIN,
  AUTO_CANCEL_EXTRA_INTERVALS,
  AUTO_CANCEL_INTERVAL,
  DEFAULT_PREPAY_CANCELLED_TEXT,
  DEFAULT_PREPAY_REMINDER_TEXT,
  DEFAULT_PREPAY_REQUIRED_TEXT,
  DEFAULT_WIDGET_MESSAGE,
  DEFAULT_WIDGET_MESSAGE_AUTO,
  MIN_PERSONS,
  NOTIFICATION_TEMPLATE_MAX_LENGTH,
  PREPAY_REQUIRED_TEMPLATE_MAX_LENGTH,
  REMINDER_DELAY_EXTRA_INTERVALS,
  REMINDER_DELAY_INTERVAL,
  REMINDER_DELAY_MAX,
  REMINDER_DELAY_MIN,
  TEMPLATE_VARIABLES,
  WIDGET_MESSAGE_MAX_LENGTH,
} from '../lib/constants';
import {
  DEFAULT_FORM_VALUES,
  mapDomainToForm,
  mapFormToDomain,
  PrepaySettingsFormValues,
  prepaySettingsValidationSchema,
} from '../lib/form';
import { PrepayAcceptanceSchedule } from './PrepayAcceptanceSchedule';

const useStyles = makeStyles((theme) => ({
  fieldset: {
    marginBottom: theme.spacing(3),
  },
  pageTitle: {
    padding: theme.spacing(2, 3),
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  content: {
    padding: theme.spacing(3),
  },
  fieldRow: {
    marginBottom: theme.spacing(3),
  },
  fieldTitle: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
  },
  checkboxFormControl: {
    minWidth: '100%',
  },
  selectFormControl: {
    minWidth: '100%',
    maxWidth: '100%',
  },
  selectItem: {
    whiteSpace: 'break-spaces',
  },
  helpText: {
    marginBottom: theme.spacing(3),
    // padding: theme.spacing(2),
    // backgroundColor: theme.palette.grey[50],
    // borderRadius: theme.shape.borderRadius,
  },
  helpTextTitle: {
    marginBottom: theme.spacing(2),
  },
  variableRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  variableChip: {
    // borderRadius: 4,
    borderRadius: theme.shape.borderRadius,
  },
  variableDescription: {
    // fontSize: '0.875rem',
    // color: theme.palette.text.secondary,
    color: theme.palette.grey[500],
  },
  checkboxWithInput: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  delayInput: {
    width: 75,
  },
  notificationRow: {
    marginBottom: theme.spacing(2),
  },
  widgetRow: {
    marginBottom: theme.spacing(2),
  },
  notificationHeader: {
    marginBottom: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
  },
  warningBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    // backgroundColor: 'rgba(253, 239, 170, 1)',
    backgroundColor: '#FFEE8F',
    borderRadius: theme.shape.borderRadius,
    width: 'fit-content',
  },
  hide: {
    display: 'none',
  },
  label: {
    [theme.breakpoints.down(960)]: {
      whiteSpace: 'nowrap',
    },
  },
}));

interface FieldsetProps {
  title?: string;
  children: React.ReactNode;
}

const Fieldset = ({ title, children }: FieldsetProps) => {
  const classes = useStyles();
  return (
    <Paper elevation={3} className={classes.fieldset}>
      {title && (
        <Typography variant="h6" className={classes.pageTitle}>
          {title}
        </Typography>
      )}
      <div className={classes.content}>{children}</div>
    </Paper>
  );
};

const reminderDelayMinutesOptions = buildMinutesRangeOptions(
  REMINDER_DELAY_MIN,
  REMINDER_DELAY_MAX,
  REMINDER_DELAY_INTERVAL,
  REMINDER_DELAY_EXTRA_INTERVALS,
);

const autoCancelDelayMinutesOptions = buildMinutesRangeOptions(
  AUTO_CANCEL_DELAY_MIN,
  AUTO_CANCEL_DELAY_MAX,
  AUTO_CANCEL_INTERVAL,
  AUTO_CANCEL_EXTRA_INTERVALS,
);

export const PrepaySettingsForm = () => {
  const classes = useStyles();
  const hasAnyConnectedGateway = useHasAnyConnectedGateway();
  const dispatch = useDispatch();

  const { data: prepaySettings, isLoading, isError } = usePrepaySettings();
  const { data: allTables = [], isLoading: allTablesIsLoading } = useAllTables();
  const updateMutation = useUpdatePrepaySettingsMutation();

  const maxCapacity = useMemo(() => getMaxCapacity(allTables), [allTables]);

  const initialFormValues = useMemo(
    () => (prepaySettings ? mapDomainToForm(prepaySettings) : DEFAULT_FORM_VALUES),
    [prepaySettings],
  );

  const formik = useFormik<PrepaySettingsFormValues>({
    initialValues: initialFormValues,
    validationSchema: prepaySettingsValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const domainData = mapFormToDomain(values);
      updateMutation.mutate(domainData);
    },
  });

  const save = (overrideValues?: Partial<PrepaySettingsFormValues>) => {
    const values = overrideValues ? { ...formik.values, ...overrideValues } : formik.values;

    prepaySettingsValidationSchema
      .validate(values)
      .then(() => {
        const domainData = mapFormToDomain(values);
        updateMutation.mutate(domainData);
      })
      .catch((error) => {
        console.error('Validation error:', error);
      });
  };

  function handleValueFieldBlur<
    T extends keyof PrepaySettingsFormValues = keyof PrepaySettingsFormValues,
  >(field: T, condition: (value: (typeof formik.values)[T]) => (typeof formik.values)[T]) {
    return async (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      formik.handleBlur(e);

      const currentValue = formik.values[field];
      const nextValue = condition(formik.values[field]);

      if (nextValue !== currentValue) {
        await formik.setFieldValue(field, nextValue);
      }

      if (nextValue !== initialFormValues[field]) {
        save({ [field]: nextValue });
      }
    };
  }

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    formik.setFieldValue(name, checked);
    save({ [name]: checked });
  };

  const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    if (name) {
      formik.setFieldValue(name, value);
      save({ [name]: value });
    }
  };

  const handleChangeEventFields = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { name, value } = e.target;

    if (!name) {
      return;
    }

    const isReminderDelayMinutesField = name === 'reminderDelayMinutes';

    const reminderDelayMinutesFieldValue = isReminderDelayMinutesField
      ? Number(value)
      : formik.values.reminderDelayMinutes;

    const autoCancelDelayMinutesFieldValue = isReminderDelayMinutesField
      ? formik.values.autoCancelDelayMinutes
      : Number(value);

    if (isNaN(reminderDelayMinutesFieldValue) || isNaN(autoCancelDelayMinutesFieldValue)) {
      dispatch(customErrorShackBar('Ошибка изменения поля'));
      return;
    }

    // Если время напоминания больше времени отмены, то уменьшаем время напоминания на 5 минут
    if (reminderDelayMinutesFieldValue >= autoCancelDelayMinutesFieldValue) {
      formik.setFieldValue('reminderDelayMinutes', autoCancelDelayMinutesFieldValue - 5);
      save({ reminderDelayMinutes: autoCancelDelayMinutesFieldValue - 5 });

      if (isReminderDelayMinutesField) {
        return;
      }
    }

    handleSelectChange(e);
  };

  if (isLoading || allTablesIsLoading) {
    return <CircularProgress />;
  }

  if (isError || !prepaySettings) {
    return (
      <Fieldset>
        <Typography className={classes.errorText}>
          Ошибка. Пожалуйста, обратитесь в поддержку, если перезагрузка страницы не помогает решить
          проблему.
        </Typography>
      </Fieldset>
    );
  }

  return (
    <>
      <Fieldset title="Настройки предоплаты">
        {!hasAnyConnectedGateway && (
          <Box className={classes.warningBox}>
            <Typography variant="body1">Не подключен ни один из шлюзов уведомлений</Typography>
            {/* TODO Добавить ссылку */}
            <Typography variant="body2" color="textSecondary">
              Нужно включить в настройках
            </Typography>
          </Box>
        )}

        <FormControlLabel
          control={
            <Switch
              name="isEnabled"
              color="primary"
              checked={formik.values.isEnabled}
              onChange={handleSwitchChange}
            />
          }
          label={<Typography variant="body1">Приём депозитов (предоплат)</Typography>}
          disabled={!hasAnyConnectedGateway}
        />
      </Fieldset>

      {hasAnyConnectedGateway && formik.values.isEnabled && (
        <>
          <Fieldset title="Способ приема платежей">
            <Box className={classes.fieldRow}>
              <FormControl className={classes.selectFormControl}>
                <InputLabel id="payment-bank-label" className={classes.label}>
                  Способ приема платежей
                </InputLabel>
                <Select
                  labelId="payment-bank-label"
                  name="paymentBank"
                  value={formik.values.paymentBank}
                  onChange={handleSelectChange}
                  label="Способ приема платежей"
                >
                  {PAYMENT_BANKS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box className={classes.fieldRow}>
              <FormControl className={classes.selectFormControl}>
                <InputLabel id="prepay-amount-mode-label" className={classes.label}>
                  Расчет суммы депозита
                </InputLabel>
                <Select
                  labelId="prepay-amount-mode-label"
                  name="prepayAmountMode"
                  value={formik.values.prepayAmountMode}
                  onChange={handleSelectChange}
                  label="Расчет суммы депозита"
                >
                  {PREPAY_AMOUNT_MODES.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      className={classes.selectItem}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {formik.values.prepayAmountMode === 'AUTO_PERSONS' && (
              <Box className={classes.fieldRow}>
                <TextField
                  label="Сумма предоплаты на одного гостя в рублях"
                  name="amountPerPerson"
                  fullWidth
                  type="number"
                  value={formik.values.amountPerPerson}
                  onChange={formik.handleChange}
                  inputProps={{ min: AMOUNT_PER_GUEST_MIN, max: AMOUNT_PER_GUEST_MAX }}
                  error={formik.touched.amountPerPerson && Boolean(formik.errors.amountPerPerson)}
                  onBlur={(e) =>
                    handleValueFieldBlur(e.target.name as 'amountPerPerson', (value) => {
                      if (value < AMOUNT_PER_GUEST_MIN) {
                        return AMOUNT_PER_GUEST_MIN;
                      }

                      if (value > AMOUNT_PER_GUEST_MAX) {
                        return AMOUNT_PER_GUEST_MAX;
                      }

                      return value;
                    })(e)
                  }
                />
              </Box>
            )}

            {formik.values.prepayAmountMode === 'AUTO_PERSONS' && (
              <Box className={classes.fieldRow}>
                <FormControl className={classes.selectFormControl}>
                  <InputLabel
                    id="minimum-guests-include-prepayment-label"
                    className={classes.label}
                  >
                    Минимальное количество гостей для включения предоплаты
                  </InputLabel>
                  <Select
                    labelId="minimum-guests-include-prepayment-labe"
                    name="minPersons"
                    value={formik.values.minPersons}
                    onChange={handleSelectChange}
                    label="Минимальное количество гостей для включения предоплаты"
                    error={formik.touched.minPersons && Boolean(formik.errors.minPersons)}
                    inputProps={{ min: MIN_PERSONS, max: maxCapacity }}
                  >
                    {new Array(maxCapacity).fill('').map((_, index) => (
                      <MenuItem key={index} value={index + 1}>
                        {index + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {formik.values.prepayAmountMode === 'AUTO_PERSONS' && <PrepayAcceptanceSchedule />}
          </Fieldset>

          <Fieldset title="События по времени">
            <Box className={classes.fieldRow}>
              <Typography className={classes.fieldTitle}>Напоминания об оплате</Typography>
              <FormControl className={classes.selectFormControl}>
                <InputLabel id="reminder-delay-minutes-label" className={classes.label}>
                  Напоминаем гостю о том, что он не внес предоплату через
                </InputLabel>
                <Select
                  labelId="reminder-delay-minutes-label"
                  name="reminderDelayMinutes"
                  value={formik.values.reminderDelayMinutes}
                  onChange={handleChangeEventFields}
                  label="Напоминаем гостю о том, что он не внес предоплату через"
                >
                  {reminderDelayMinutesOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.hWithDeclension}
                      {option.mWithDeclension}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box className={classes.fieldRow}>
              <Typography className={classes.fieldTitle}>Автоматическая отмена резерва</Typography>

              <FormControl className={classes.selectFormControl}>
                <InputLabel id="auto-cancel-delay-minutes-label" className={classes.label}>
                  Автоматически отменяем резерв через
                </InputLabel>
                <Select
                  labelId="auto-cancel-delay-minutes-label"
                  name="autoCancelDelayMinutes"
                  value={formik.values.autoCancelDelayMinutes}
                  onChange={handleChangeEventFields}
                  label="Автоматически отменяем резерв через"
                >
                  {autoCancelDelayMinutesOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.hWithDeclension}
                      {option.mWithDeclension}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Fieldset>

          <Fieldset title="Виджет">
            <TextField
              name="widgetMessage"
              label="Сообщение для гостей в виджете"
              fullWidth
              multiline
              disabled={formik.values.prepayAmountMode === 'AUTO_PERSONS'}
              className={formik.values.prepayAmountMode === 'AUTO_PERSONS' ? classes.hide : ''}
              value={formik.values.widgetMessage}
              onChange={formik.handleChange}
              onBlur={(e) =>
                handleValueFieldBlur(e.target.name as 'widgetMessage', (value) =>
                  !value.trim() ? DEFAULT_WIDGET_MESSAGE : value,
                )(e)
              }
              inputProps={{ maxLength: WIDGET_MESSAGE_MAX_LENGTH }}
              error={formik.touched.widgetMessage && Boolean(formik.errors.widgetMessage)}
              helperText={
                (formik.touched.widgetMessage && formik.errors.widgetMessage) ||
                `${formik.values.widgetMessage.length}/${WIDGET_MESSAGE_MAX_LENGTH}`
              }
            />
            <TextField
              name="widgetMessageAuto"
              label="Сообщение для гостей в виджете"
              fullWidth
              multiline
              disabled={formik.values.prepayAmountMode === 'MANUAL'}
              className={formik.values.prepayAmountMode === 'MANUAL' ? classes.hide : ''}
              value={formik.values.widgetMessageAuto}
              onChange={formik.handleChange}
              onBlur={(e) =>
                handleValueFieldBlur(e.target.name as 'widgetMessageAuto', (value) =>
                  !value.trim() ? DEFAULT_WIDGET_MESSAGE_AUTO : value,
                )(e)
              }
              inputProps={{ maxLength: WIDGET_MESSAGE_MAX_LENGTH }}
              error={formik.touched.widgetMessageAuto && Boolean(formik.errors.widgetMessageAuto)}
              helperText={
                (formik.touched.widgetMessageAuto && formik.errors.widgetMessageAuto) ||
                `${formik.values.widgetMessageAuto?.length || 0}/${WIDGET_MESSAGE_MAX_LENGTH}`
              }
            />
          </Fieldset>

          <Fieldset title="Уведомления">
            <Box className={classes.helpText}>
              <Typography className={classes.helpTextTitle}>
                Вы можете задавать переменные в текстах сообщений:
              </Typography>
              {TEMPLATE_VARIABLES.map(({ variable, description }) => (
                <Box key={variable} className={classes.variableRow}>
                  <Chip label={variable} size="small" className={classes.variableChip} />
                  <Typography className={classes.variableDescription}>— {description}</Typography>
                </Box>
              ))}
            </Box>

            <Box className={classes.notificationRow}>
              <TextField
                name="prepayRequiredTemplate"
                fullWidth
                multiline
                label="Уведомление о необходимости внести предоплату"
                value={formik.values.prepayRequiredTemplate}
                onChange={formik.handleChange}
                onBlur={(e) =>
                  handleValueFieldBlur(e.target.name as 'prepayRequiredTemplate', (value) =>
                    !value.trim() ? DEFAULT_PREPAY_REQUIRED_TEXT : value,
                  )(e)
                }
                inputProps={{ maxLength: PREPAY_REQUIRED_TEMPLATE_MAX_LENGTH }}
                error={
                  formik.touched.prepayRequiredTemplate &&
                  Boolean(formik.errors.prepayRequiredTemplate)
                }
                helperText={
                  (formik.touched.prepayRequiredTemplate && formik.errors.prepayRequiredTemplate) ||
                  `${formik.values.prepayRequiredTemplate.length}/${PREPAY_REQUIRED_TEMPLATE_MAX_LENGTH}`
                }
              />
            </Box>

            <Box className={classes.notificationRow}>
              <TextField
                name="prepayReminderTemplate"
                fullWidth
                multiline
                label="Напоминание о предоплате, если гость ее не внес"
                value={formik.values.prepayReminderTemplate}
                onChange={formik.handleChange}
                onBlur={(e) =>
                  handleValueFieldBlur(e.target.name as 'prepayReminderTemplate', (value) =>
                    !value.trim() ? DEFAULT_PREPAY_REMINDER_TEXT : value,
                  )(e)
                }
                disabled={!formik.values.reminderEnabled}
                inputProps={{ maxLength: NOTIFICATION_TEMPLATE_MAX_LENGTH }}
                error={
                  formik.touched.prepayReminderTemplate &&
                  Boolean(formik.errors.prepayReminderTemplate)
                }
                helperText={
                  (formik.touched.prepayReminderTemplate && formik.errors.prepayReminderTemplate) ||
                  `${formik.values.prepayReminderTemplate.length}/${NOTIFICATION_TEMPLATE_MAX_LENGTH}`
                }
              />
            </Box>

            <Box className={classes.notificationRow}>
              <TextField
                name="prepayCancelledTemplate"
                fullWidth
                multiline
                label="Уведомление об отмене, если предоплата не поступила"
                value={formik.values.prepayCancelledTemplate}
                onChange={formik.handleChange}
                onBlur={(e) =>
                  handleValueFieldBlur(e.target.name as 'prepayCancelledTemplate', (value) =>
                    !value.trim() ? DEFAULT_PREPAY_CANCELLED_TEXT : value,
                  )(e)
                }
                disabled={!formik.values.autoCancelEnabled}
                inputProps={{ maxLength: NOTIFICATION_TEMPLATE_MAX_LENGTH }}
                error={
                  formik.touched.prepayCancelledTemplate &&
                  Boolean(formik.errors.prepayCancelledTemplate)
                }
                helperText={
                  (formik.touched.prepayCancelledTemplate &&
                    formik.errors.prepayCancelledTemplate) ||
                  `${formik.values.prepayCancelledTemplate.length}/${NOTIFICATION_TEMPLATE_MAX_LENGTH}`
                }
              />
            </Box>
          </Fieldset>
        </>
      )}
    </>
  );
};
